import React from "react"
import { PageDrawerAction } from "src/components/ui/PageDrawerAction"
import { Icons } from "src/components/ui/Icons"
import { useActions, useAppState, useReaction } from "src/state"
import {
    Tenant_Status_Enum,
    Tenant_Type_Enum,
    TenantFragment,
    Tenants_Insert_Input,
    UpdateTenantMutationVariables
} from "src/state/gql/_types"
import { FormDrawer } from "src/components/forms/FormDrawer"
import { Loader } from "@mantine/core"
import {
    AgencyOperationsAdminVenueCreateFragment,
    AgencyOperationsAdminVenueCreateSchema,
    AgencyOperationsAdminVenueUpdateFragment,
    AgencyOperationsAdminVenueUpdateSchema
} from "src/components/agency/operations/administrator/venues/AgencyOperationsAdminVenueSchemas"
import { extractCurrentItemData } from "src/utils/forms"

type FormPayloadType = AgencyOperationsAdminVenueCreateFragment

let initialState: FormPayloadType = {
    type: Tenant_Type_Enum.EventSpace,
    name: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    country: "",
    postal_code: "",
    phone: "",
    instagram: "",
    website: "",
    poc: "",
    anniversary: "",
    timezone: 'America/New_York',
    logo: ""
};

export const CreateAgencyOperationsAdminVenueDrawer: React.FC<{ onClose(): void }> = ({ onClose }) => {

    const { createItem, updateItem, deleteItem } = useActions().tenants;
    const { loading, errors } = useAppState().tenants;

    let currentItemId = null;
    let currentItemData: FormPayloadType | null = null;

    const handleCreate = (payload: Tenants_Insert_Input) => {
        return createItem({ ...payload, id: crypto.randomUUID() })
    }

    return (
        <>
            <FormDrawer<FormPayloadType, TenantFragment, string>
                autoOpen={false}
                formSchema={AgencyOperationsAdminVenueCreateSchema}
                createAction={handleCreate}
                updateAction={updateItem}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialState}
                currentItemData={currentItemData}
                onClose={onClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const CreateAgencyOperationsAdminVenueAction = () => {
    return (
        <PageDrawerAction<TenantFragment>
            title='Add Venue'
            action={CreateAgencyOperationsAdminVenueDrawer}
            icon={Icons.add}
        />
    )
}

let initialStateUpdate: AgencyOperationsAdminVenueUpdateFragment = {
    status: Tenant_Status_Enum.Active,
    type: Tenant_Type_Enum.EventSpace,
    name: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    country: "",
    postal_code: "",
    phone: "",
    instagram: "",
    website: "",
    poc: "",
    anniversary: "",
    timezone: 'America/New_York',
    logo: ""
};

export const UpdateAgencyOperationsAdminVenueDrawer: React.FC<{ onClose(): void, deselect: boolean }> = ({ onClose, deselect = true }) => {


    const { updateItem, deleteItem, deselectItem } = useActions().tenants;
    const { currentItem, loading, errors } = useAppState().tenants;



    if (loading || !currentItem) return <Loader />

    let currentItemId = null;
    let currentItemData: AgencyOperationsAdminVenueUpdateFragment | null = null;
    if (currentItem) {
        currentItemId = currentItem.id;
        currentItemData = extractCurrentItemData<AgencyOperationsAdminVenueUpdateFragment>(initialStateUpdate, currentItem);
    }

    const handleClose = () => {
        if (deselect) {
            deselectItem()
        }
        onClose()
    }

    const handleUpdate = (payload: UpdateTenantMutationVariables) => {
        return updateItem({
            id: payload.id,
            _set: {
                ...payload._set
            }
        })
    }

    return (
        <>
            <FormDrawer<AgencyOperationsAdminVenueUpdateFragment, TenantFragment, string>
                autoOpen={false}
                formSchema={AgencyOperationsAdminVenueUpdateSchema}
                updateAction={handleUpdate}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialStateUpdate}
                currentItemData={currentItemData}
                onClose={handleClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const UpdateAgencyOperationsAdminVenueAction = () => {

    const reaction = useReaction()
    const { deselectItem } = useActions().tenants;
    const [opened, setOpened] = React.useState(false);

    React.useEffect(() => reaction(
        (state) => state.tenants.currentItemId,
        (selectedID) => {
            setOpened(!!selectedID)
        }, { immediate: false }
    ))

    const handleClose = () => {
        deselectItem()
    }

    return (
        <PageDrawerAction<TenantFragment>
            title='Edit Venue'
            action={UpdateAgencyOperationsAdminVenueDrawer}
            icon={Icons.edit}
            startOpened={opened}
            onClose={handleClose}
            hidden
        />
    )
}

export const EditAgencyOperationsAdminVenueAction = () => {
    return (
        <PageDrawerAction<TenantFragment>
            title='Edit Venue'
            action={UpdateAgencyOperationsAdminVenueDrawer}
            icon={Icons.edit}
            props={{
                deselect: false
            }}
        />
    )
}